import { useMemo } from 'react';

import { useOrganizationConcurrency } from './useConcurrency';
import { useOrganizationMemory } from './useMemoryLimit';
import { useOrganizationUsage } from './useOrganizationUsage';
import { useWorkflowLimits } from './useWorkflowLimits';
import {
  policyOf,
  workspaceLimitRows,
  type LimitsPolicy,
  type WorkspaceLimitsRow,
} from '../workspaceLimits';

/**
 * The Limits page's data (spec 117): the three organization reads it already made,
 * joined into one row per workspace.
 *
 * Spend is what the page cannot do without — its pending and failure states are the
 * page's. Workflow caps and run capacity are joined in as they arrive: a failure of
 * either leaves its part out (caps empty, runs hidden) rather than taking the whole page
 * down (memory the same), the same rule an older service without the concurrency read gets (R9).
 */
export function useWorkspaceLimits(period?: string) {
  const usage = useOrganizationUsage(period);
  const caps = useWorkflowLimits(period);
  const concurrency = useOrganizationConcurrency();
  const memory = useOrganizationMemory();

  const joined = useMemo(() => {
    if (!usage.data) return null;
    const sources = {
      usage: usage.data,
      caps: caps.data ?? [],
      concurrency: concurrency.data ?? null,
      memory: memory.data ?? null,
    };
    const rows = workspaceLimitRows(sources);
    return {
      rows,
      policy: policyOf({ ...sources, rows }),
    };
  }, [usage.data, caps.data, concurrency.data, memory.data]);

  return {
    usage,
    rows: joined?.rows ?? ([] as WorkspaceLimitsRow[]),
    policy: joined?.policy ?? (null as LimitsPolicy | null),
    /** Whether run capacity can be shown at all (R9). */
    hasRuns: Boolean(concurrency.data),
    /** Whether memory can be shown at all; an older service has no such read. */
    hasMemory: Boolean(memory.data),
  };
}
